import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Pagination, Tooltip, message } from "antd";
import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from "../../components/ui";

const { Option } = Select;

const SmsTemplates = () => {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalTemplates, setTotalTemplates] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form] = Form.useForm();

    // Fetch templates
    const fetchTemplates = async (page = 1) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/sample_sms?page=${page}`);
            setTemplates(response.data.data.data);
            setTotalTemplates(response.data.data.total);
            setCurrentPage(response.data.data.current_page);
        } catch (error) {
            message.error(t('smsTemplates.loadError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    // Handle form submission
    const handleFormSubmit = async (values) => {
        try {
            if (editingTemplate) {
                // Update template
                await axiosInstance.put(`/sample_sms/${editingTemplate.id}`, values);
                message.success(t('smsTemplates.updated'));
            } else {
                // Create template
                await axiosInstance.post("/sample_sms", values);
                message.success(t('smsTemplates.created'));
            }
            fetchTemplates(currentPage);
            setModalVisible(false);
            form.resetFields();
        } catch (error) {
            message.error(t('smsTemplates.saveError'));
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/sample_sms/${id}`);
            message.success(t('smsTemplates.deleted'));
            fetchTemplates(currentPage);
        } catch (error) {
            message.error(t('smsTemplates.deleteError'));
        }
    };

    // Open modal for editing
    const handleEdit = (template) => {
        setEditingTemplate(template);
        form.setFieldsValue(template);
        setModalVisible(true);
    };

    // Open modal for creating
    const handleAdd = () => {
        setEditingTemplate(null);
        form.resetFields();
        setModalVisible(true);
    };

    const renderType = (type) => (
        <StatusBadge tone={type === 'service' ? 'blue' : 'violet'}>{type}</StatusBadge>
    );

    const columns = [
        {
            title: t('smsTemplates.legalEntity'),
            dataIndex: "legal_entity",
            key: "legal_entity",
            render: (value) => <span className="text-sm font-medium text-slate-800">{value}</span>,
        },
        {
            title: t('smsTemplates.inn'),
            dataIndex: "inn",
            key: "inn",
            width: 140,
            render: (value) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{value}</span>
            ),
        },
        {
            title: t('smsTemplates.senderName'),
            dataIndex: "sender_name",
            key: "sender_name",
            width: 180,
            render: (value) => <span className="text-sm text-slate-700">{value}</span>,
        },
        {
            title: t('smsTemplates.templateType'),
            dataIndex: "type",
            key: "type",
            width: 150,
            render: renderType,
        },
        {
            title: t('smsTemplates.text'),
            dataIndex: "text",
            key: "text",
            render: (text) => (
                <div className="max-w-[320px] truncate text-xs text-slate-500">{text}</div>
            ),
        },
        {
            title: t('common.actions'),
            key: "actions",
            width: 88,
            fixed: 'right',
            render: (template) => (
                <div className="flex items-center gap-0.5">
                    <Tooltip title={t('common.edit')}>
                        <RowIconButton onClick={() => handleEdit(template)}>
                            <PencilSquareIcon className="w-[18px] h-[18px]" />
                        </RowIconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                        <RowIconButton onClick={() => handleDelete(template.id)}>
                            <TrashIcon className="w-[18px] h-[18px]" />
                        </RowIconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const renderMobileCard = (template) => (
        <div>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#1D2C7B] truncate">
                        {template.legal_entity}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5 truncate">
                        {template.sender_name}
                    </span>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    <RowIconButton onClick={() => handleEdit(template)} aria-label={t('common.edit')}>
                        <PencilSquareIcon className="w-[18px] h-[18px]" />
                    </RowIconButton>
                    <RowIconButton onClick={() => handleDelete(template.id)} aria-label={t('common.delete')}>
                        <TrashIcon className="w-[18px] h-[18px]" />
                    </RowIconButton>
                </div>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {renderType(template.type)}
                <span className="text-xs text-slate-600 tabular-nums">{template.inn}</span>
            </div>

            {template.text && (
                <p className="mt-1.5 mb-0 text-xs text-slate-500 line-clamp-2">{template.text}</p>
            )}
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('smsTemplates.title')}
                    count={totalTemplates}
                    actions={
                        <ActionButton icon={<PlusIcon className="w-4 h-4" />} onClick={handleAdd}>
                            {t('common.add')}
                        </ActionButton>
                    }
                />

                <DataTable
                    dataSource={templates}
                    columns={columns}
                    loading={loading}
                    mobileCard={renderMobileCard}
                    scrollX={1000}
                    pagination={false}
                />

                <div className="flex justify-center sm:justify-end border-t border-slate-100 px-3 py-2.5">
                    <Pagination
                        size="small"
                        current={currentPage}
                        total={totalTemplates}
                        pageSize={20}
                        onChange={(page) => fetchTemplates(page)}
                    />
                </div>
            </PageCard>

            <Modal
                centered
                width="100%"
                style={{ maxWidth: 520 }}
                title={editingTemplate ? t('sms.editTemplate') : t('sms.addTemplate')}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                okText={t('common.save')}
                cancelText={t('common.cancel')}
            >
                <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        <Form.Item
                            name="legal_entity"
                            label={t('smsTemplates.legalEntity')}
                            rules={[{ required: true, message: t('common.required') }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="inn"
                            label={t('smsTemplates.inn')}
                            rules={[{ required: true, message: t('common.required') }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="sender_name"
                            label={t('smsTemplates.senderName')}
                            rules={[{ required: true, message: t('common.required') }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="type"
                            label={t('smsTemplates.templateType')}
                            rules={[{ required: true, message: t('common.required') }]}
                        >
                            <Select>
                                <Option value="service">{t('smsTemplates.service')}</Option>
                                <Option value="transaction">{t('smsTemplates.transaction')}</Option>
                            </Select>
                        </Form.Item>
                    </div>
                    <Form.Item
                        name="text"
                        label={t('smsTemplates.text')}
                        rules={[{ required: true, message: t('common.required') }]}
                    >
                        <Input.TextArea rows={4} />
                    </Form.Item>
                </Form>
            </Modal>
        </PageShell>
    );
};

export default SmsTemplates;
